/**
 * LegalPage.jsx — PUBLIC yasal belge sayfası (KVKK / Gizlilik Politikası / Kullanım Şartları)
 *
 * Route: /legal/:docType  (docType: kvkk | privacy | terms)
 * Auth: GEREKMEZ — bu sayfa App.js'te ProtectedRoute/RoleRoute'a SARILMAZ, standalone
 * public bir sayfadır (global Navbar de gizlenir — bkz. App.js isLegalRoute).
 *
 * Google Play Store gizlilik politikası URL'si olarak kullanılacak:
 *   https://kariyeronline.tr/legal/privacy
 *
 * Backend: GET /legal/{doc_type}?lang=tr|en (routes/legal_content.py, auth gerektirmez)
 *   → { available:false, doc_type, lang } veya
 *     { available:true, title, content, format, version, effective_date, updated_at }
 *
 * İçerik render: format='markdown' → renderMarkdownPreview, format='html' →
 * sanitizeHtmlPreview (utils/legalContentRender.js — AdminLegalContent.jsx ile TEK
 * paylaşılan sanitize noktası, DOMPurify tabanlı, backend bleach whitelist'iyle hizalı).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiErrorMessage } from '../utils/apiError';
import { sanitizeHtmlPreview, renderMarkdownPreview } from '../utils/legalContentRender';
import useReducedMotion from '../hooks/useReducedMotion';
import Logo from '../components/Logo';
import {
  ArrowLeft,
  ShieldCheck,
  FileText,
  Scroll,
  Loader2,
  AlertTriangle,
  Clock,
  Calendar,
  FileQuestion,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const VALID_DOC_TYPES = ['kvkk', 'privacy', 'terms'];
const VALID_LANGS = ['tr', 'en'];

const DOC_TYPE_ICON = {
  kvkk: ShieldCheck,
  privacy: FileText,
  terms: Scroll,
};

function fmtDate(val, lang) {
  if (!val) return null;
  try {
    return new Date(val).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return val;
  }
}

export default function LegalPage() {
  const { docType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang: uiLang, t } = useLanguage();
  const reduced = useReducedMotion();

  const isValidDocType = VALID_DOC_TYPES.includes(docType);

  // Dil önceliği: URL ?lang= > global UI dili (localStorage 'preferred_language',
  // ilk ziyarette 'tr' varsayılan — dolaylı olarak tarayıcı/kullanıcı tercihini yansıtır).
  const langParam = searchParams.get('lang');
  const contentLang = VALID_LANGS.includes(langParam) ? langParam : uiLang;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(isValidDocType);
  const [error, setError] = useState(null);

  const fetchDoc = useCallback(async () => {
    if (!isValidDocType) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/legal/${docType}`, {
        params: { lang: contentLang },
      });
      setDoc(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, uiLang));
    } finally {
      setLoading(false);
    }
  }, [docType, contentLang, isValidDocType, uiLang]);

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  useEffect(() => {
    if (doc?.available && doc?.title) {
      document.title = `${doc.title} · Kariyer Online`;
    } else if (isValidDocType) {
      document.title = `${t(`legal.docType.${docType}`)} · Kariyer Online`;
    } else {
      document.title = 'Kariyer Online';
    }
    return () => {
      document.title = 'Kariyer Online';
    };
  }, [doc, docType, isValidDocType, t]);

  const contentHtml = useMemo(() => {
    if (!doc?.available || !doc?.content) return '';
    return doc.format === 'html' ? sanitizeHtmlPreview(doc.content) : renderMarkdownPreview(doc.content);
  }, [doc]);

  const switchLang = (nextLang) => {
    const next = new URLSearchParams(searchParams);
    next.set('lang', nextLang);
    setSearchParams(next, { replace: true });
  };

  const Icon = isValidDocType ? DOC_TYPE_ICON[docType] : FileQuestion;

  return (
    <div className="min-h-screen duo-bg flex flex-col" data-testid="legal-page">
      <header className="border-b-2 border-[var(--duo-stroke)] px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex" data-testid="legal-logo-home-link">
            <Logo size={28} />
          </Link>
          <div className="flex items-center gap-1" role="group" aria-label={t('legal.langToggle.label')}>
            {VALID_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                data-testid={`legal-lang-toggle-${l}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                  contentLang === l
                    ? 'bg-[var(--duo-blue)] text-white'
                    : 'text-[var(--duo-text-soft)] hover:bg-[var(--duo-sky)]'
                }`}
              >
                {t(`legal.langToggle.${l}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-10">
        <motion.div
          className="max-w-3xl mx-auto bg-white rounded-2xl p-6 sm:p-10"
          style={{ border: '2px solid var(--duo-stroke)' }}
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          {!isValidDocType ? (
            <div className="text-center py-10" data-testid="legal-invalid-doctype">
              <FileQuestion size={48} className="mx-auto mb-4 text-[var(--duo-text-light)]" />
              <h1 className="font-duo-display text-2xl text-[var(--duo-text)] mb-2">
                {t('legal.notFound.title')}
              </h1>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-6">
                {t('legal.notFound.body')}
              </p>
              <Link to="/" className="btn-duo-secondary inline-flex" data-testid="legal-back-home-link">
                <ArrowLeft size={16} />
                {t('legal.backHome')}
              </Link>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="legal-loading">
              <Loader2 size={32} className="animate-spin text-[var(--duo-blue)]" />
              <p className="text-sm text-[var(--duo-text-soft)] font-medium">{t('legal.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-10" data-testid="legal-error">
              <AlertTriangle size={48} className="mx-auto mb-4 text-[var(--duo-error)]" />
              <h1 className="font-duo-display text-2xl text-[var(--duo-text)] mb-2">
                {t('legal.error.title')}
              </h1>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-6">{error}</p>
              <button type="button" onClick={fetchDoc} className="btn-duo-secondary inline-flex" data-testid="legal-retry-btn">
                {t('legal.error.retry')}
              </button>
            </div>
          ) : doc && doc.available === false ? (
            <div className="text-center py-10" data-testid="legal-unavailable">
              <Icon size={48} className="mx-auto mb-4 text-[var(--duo-text-light)]" />
              <h1 className="font-duo-display text-2xl text-[var(--duo-text)] mb-2">
                {t(`legal.docType.${docType}`)}
              </h1>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-6">
                {t('legal.unavailable.body')}
              </p>
              <Link to="/" className="btn-duo-secondary inline-flex" data-testid="legal-back-home-link">
                <ArrowLeft size={16} />
                {t('legal.backHome')}
              </Link>
            </div>
          ) : doc && doc.available ? (
            <article data-testid="legal-content">
              <div className="flex items-center gap-2 mb-2 text-[var(--duo-blue)]">
                <Icon size={20} />
                <span className="text-xs font-bold uppercase tracking-wider">
                  {t(`legal.docType.${docType}`)}
                </span>
              </div>
              <h1 className="font-duo-display text-2xl sm:text-3xl text-[var(--duo-text)] mb-3" data-testid="legal-title">
                {doc.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-6 text-xs text-[var(--duo-text-soft)] font-medium">
                {doc.effective_date && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} />
                    {t('legal.effectiveDate')}: {fmtDate(doc.effective_date, uiLang)}
                  </span>
                )}
                {doc.updated_at && (
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} />
                    {t('legal.updatedAt')}: {fmtDate(doc.updated_at, uiLang)}
                  </span>
                )}
              </div>
              <div
                className="legal-doc-content"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </article>
          ) : null}
        </motion.div>

        {isValidDocType && doc?.available && (
          <div className="max-w-3xl mx-auto mt-6 text-center">
            <Link to="/" className="text-sm font-bold text-[var(--duo-blue)] hover:underline inline-flex items-center gap-1.5" data-testid="legal-back-home-link-bottom">
              <ArrowLeft size={14} />
              {t('legal.backHome')}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
